import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";
import ScrollContainer from "react-indiana-drag-scroll";
import config from "../../config";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";
import NoDataFound from "../../shared/NoDataFound";
import TableLoader from "../../shared/TableLoader";
import { alphaformatDateTime } from "../../utils/dateFormat";
import DetailDrawer from "../ContentWriter/OrderBooking/admin/components/DetailDrawer";
import ConfirmDialog from "../ContentWriter/OrderBooking/admin/components/ConfirmDialog";
import DesignerAssignControl from "../ContentWriter/OrderBooking/admin/components/DesignerAssignControl";
import ContentWriterAssignControl from "../ContentWriter/OrderBooking/admin/components/ContentWriterAssignControl";
import { TABS as CONTENT_STATUS_TABS, canApprove, canReject } from "../ContentWriter/OrderBooking/admin/config";
import { approveContent, rejectContent, fetchContentWriters, assignContentWriter } from "../ContentWriter/OrderBooking/admin/api";
import { CREATIVE_STATUS, STATUS_META, StatusChip } from "./creativeStatus";
import { adunitPriceRows } from "./bookingPrices";
import Toggle from "../../utils/Toggle";
import "./BookingCreativesAssign.scss";

const SORT_BY_LATEST_ACTIVITY_KEY = "view_booking_creatives_by_latest_activity";

/**
 * CST view: "Creative Order Booking" (from the Order Booking flow). Split into
 * three tabs:
 *   - Orders    : every booking-slot order (picked + unpicked). Shows budget
 *                 (ro_value), accumulated geo targets, RO download, and a details
 *                 drawer with per-ad-unit details + a creatives preview. Unpicked
 *                 orders are picked in place from the drawer, which calls
 *                 /slotbooking/create-slot-order (generates the order, its ad-serving
 *                 creatives, and line items) — no redirect to the old slot screen.
 *   - New Device Login Access : the queue of order-booking sign-ins refused
 *                 because the handset was not the one bound to the account
 *                 (`sales_app_user_new_device_requests`). Allowing one clears the
 *                 bound `mid` — snapshotting the old device into
 *                 `sales_app_user_device_history` — so the sales person's NEXT
 *                 sign-in binds their new phone. It does not bind it here; the
 *                 order-booking service stays the only writer of that value.
 *   - Creatives : the assign-designer / assign-content-writer table. CST picks a
 *                 designer AND (separately) a content writer per creative, and
 *                 reviews the content writer's submitted drafts and any pending
 *                 creative-type/design-change requests via the shared
 *                 `ContentWriter/OrderBooking/admin` `DetailDrawer` — the same
 *                 detail/timeline view the (now-retired) `/content-admin/order-booking`
 *                 screen used. Sourced from `booking_slot_creatives` (bsc_id),
 *                 mirroring the main-ads `AssignToDesignerAds` flow for the designer
 *                 side.
 *
 * Backend contract (all POST, on config.BASE_URL, under the /slotbooking route):
 *   /slotbooking/get-booking-orders      -> { code, data:{ data:[orderRow], total } }
 *   /slotbooking/get-booking-creatives   -> { code, data:{ data:[creativeRow], total } }
 *       (accepts optional slot_id to scope creatives to one order; also accepts
 *       a `content_status` filter and returns content_status/content_writer_name/
 *       content_instructions/content_variations/content_assigned_by_name per the
 *       booking_slot_creative_content join — see toContentDrawerRow below for the
 *       shape adaptation this screen does on top of that row)
 *   /slotbooking/get-booking-filter-options -> { code, data:{ salesPersons:[{value,label}], locations:[{value,label}] } }
 *       (picker data shared by both tabs — see useSalesFilterOptions/SalesFilterFields)
 *   /alpha_cst_designer/getDesigners   -> { code, data:{ data:[{designer_id,name}] } }
 *   /slotbooking/assign-designer       -> { code, data:{...} }
 *   /slotbooking/get-device-access-requests -> { code, data:{ data:[requestRow], total } }
 *   /slotbooking/decide-device-access  -> { code, data:{ undr_id, status, released } }
 * requestRow: { undr_id, sales_person:{user_id,name,email,mobile,team}, requested_mid,
 *   bound_mid, current_mid (null once released), ip, attempts, status(0 pending·
 *   1 allowed·2 rejected), decided_by, decided_by_name, decided_at, created_at,
 *   updated_at }
 *
 * Both tabs accept `salesPersonIds`/`locationIds` (int[], both optional,
 * multi-select) — two SEPARATE, independent filters AND'd together when both
 * are given (each is itself an OR: "any of these people"/"any of these
 * locations"). `locationIds` matches `sales_persons.location`, not a
 * geo-targeting field on the order/creative itself.
 * orderRow: { order_id(slot_id), order_ref, order_status, ro_value, rofile_url,
 *   picked, cst_name, brand_name, client_name(company), campaign_name, sales_person,
 *   start_date, end_date, created_time, targets:{languages,states,districts,constituencies} }
 *
 * Content-writer assignment / draft review is a SEPARATE backend contract (the
 * `booking_content_admin` service, base path `/user/booking-content-admin/*`,
 * see `../ContentWriter/OrderBooking/admin/api.js` / `config.js`) that this
 * screen calls directly by `bsc_id` — it does not go through `/slotbooking/*`.
 */

// A designer can be assigned or reassigned until the creative is FINAL.
// STATUS_OPTIONS stays here (UI-filter-specific) even though CREATIVE_STATUS/
// STATUS_META/StatusChip themselves moved to ./creativeStatus.
const STATUS_OPTIONS = [
  { value: null, label: "All Statuses" },
  ...Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label })),
];

// Content-status filter options — reuses the content-admin panel's own status
// model/labels (TABS already carries { key, label, status } for statuses 0-5)
// so this is not a second label source living alongside STATUS/getStatus.
const CONTENT_STATUS_OPTIONS = [
  { value: null, label: "All Content Statuses" },
  ...CONTENT_STATUS_TABS.map((t) => ({ value: t.status, label: t.label })),
];

// booking creative_type (broad category) -> friendly ad-type label.
const AD_TYPE_LABELS = {
  image: "Image",
  video: "Video",
  promotedStory: "Promoted Story",
};

// booking sub_type -> friendly creative-type label.
const SUBTYPE_LABELS = {
  full_image: "Full Image",
  full_video: "Full Video",
  promoted_image_story: "Promoted Image Story",
  promoted_video_story: "Promoted Video Story",
};

// Ad-type filter options — same source of truth as the table's "Ad Type" column.
const AD_TYPE_OPTIONS = [
  { value: null, label: "All Ad Types" },
  ...Object.entries(AD_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

// Creative-type (sub_type) filter options — same source of truth as the table's
// "Creative Type" column.
const SUBTYPE_OPTIONS = [
  { value: null, label: "All Creative Types" },
  ...Object.entries(SUBTYPE_LABELS).map(([value, label]) => ({ value, label })),
];

const dateCell = (epoch) => {
  const { dateStr, timeStr } = alphaformatDateTime(epoch);
  if (dateStr === "-") return <span className="bc-muted">-</span>;
  return (
    <span className="bc-date">
      {dateStr}
      <span className="bc-time"> | {timeStr}</span>
    </span>
  );
};

const salesPersonCell = (row) => {
  const sp = row.sales_person || {};
  return (
    <div className="bc-person">
      <span className="bc-person__name">
        <sl-icon library="lucide" name="user" style={{ fontSize: "14px" }}></sl-icon>
        {sp.name || "-"}
      </span>
      {sp.mobile && (
        <span className="bc-person__phone">
          <sl-icon library="lucide" name="phone" style={{ fontSize: "12px" }}></sl-icon>
          {sp.mobile}
        </span>
      )}
    </div>
  );
};

const creativeLabel = (row) => (row ? row.creative_name || `Creative #${row.bsc_id}` : "");

/**
 * Adapts a merged `/slotbooking/get-booking-creatives` row onto the flat shape
 * `DetailDrawer` (built for `booking_content_admin`'s `/list` row)
 * expects: `instructions`/`variations`/`assigned_by_name` unprefixed (this
 * endpoint sends them prefixed as `content_instructions`/`content_variations`/
 * `content_assigned_by_name` to avoid colliding with the design-side fields on
 * the same row), plus `sales_person_name`/`sales_person_id` split out of this
 * endpoint's nested `sales_person` object. Everything else (bsc_id,
 * creative_name, sub_type, creative_type, img_urls, video_urls, head_line,
 * description, text_in_link, created_time, content_status, content_writer_id,
 * content_writer_name) already matches by name and passes through via the
 * spread. `assigned_by_id` / `assigned_time` are not part of the documented
 * contract extension — if the backend ever adds them under those exact names
 * they will flow through automatically; until then DetailDrawer's Info tab
 * just renders "—" for them.
 */
const toContentDrawerRow = (row) => {
  if (!row) return null;
  return {
    ...row,
    instructions: row.content_instructions ?? null,
    variations: row.content_variations ?? [],
    assigned_by_name: row.content_assigned_by_name ?? null,
    sales_person_name: row.sales_person?.name ?? null,
    sales_person_id: row.sales_person?.sales_person_id ?? null,
  };
};

/* booking_slots.status (see backend slotStatusOptions). */
const ORDER_STATUS_META = {
  1: { label: "Pending", color: "bg-pending text-pending_text" },
  2: { label: "Finance Pending", color: "bg-blue-100 text-blue-800" },
  3: { label: "Approved", color: "bg-done text-done_text" },
  4: { label: "Rejected", color: "bg-reject text-reject_text" },
  5: { label: "Cancelled", color: "bg-gray-100 text-gray-800" },
};

const OrderStatusChip = ({ status }) => {
  if (status == null) return <span className="bc-muted">-</span>;
  const meta = ORDER_STATUS_META[status] || { label: "Unknown", color: "bg-gray-100 text-gray-800" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${meta.color}`}>
      {meta.label}
    </span>
  );
};

// Picked = a CST has claimed the order (backend approval_obj.cst_id).
const PickedBadge = ({ order }) =>
  order.picked ? (
    <span className="bc-count bc-count--ok" title={order.cst_name || ""}>
      Picked{order.cst_name ? ` · ${order.cst_name}` : ""}
    </span>
  ) : (
    <span className="bc-count bc-count--warn">Not picked</span>
  );

// Booked in the order-booking app with no creatives (booking_slots.creatives_by_cst):
// the CST executes it as-is — order, line items and targets, no creatives.
const CstCreativesBadge = ({ order }) =>
  order.creatives_by_cst ? (
    <span className="bc-count bc-count--warn" title="Booked without creatives — executed as-is">
      Creatives by CST
    </span>
  ) : null;

const formatMoney = (v) => (v != null && v !== "" ? `₹${Number(v).toLocaleString("en-IN")}` : "-");

// Ad-unit display helpers (mirror salesBooking SlotVIew formatting).
const SLOT_LABELS = { 0: "A", 1: "B", 2: "C" };
const formatPositions = (positions) =>
  (positions || []).map((p) => (p === -1 ? "SOV" : `P${p}`)).join(", ") || "-";
const formatDateRange = (dr) =>
  `${alphaformatDateTime(dr.start_date).dateStr} → ${alphaformatDateTime(dr.end_date).dateStr}`;

// Merged (accumulated) geo targets of an order rendered as chips. The parent cell
// scrolls horizontally when they overflow; the drawer variant (wrap) lets them wrap.
// Locations first (the actual served geography), language last.
const TARGET_GROUPS = [
  { key: "states", cls: "bc-chip--state" },
  { key: "districts", cls: "bc-chip--dist" },
  { key: "mandals", cls: "bc-chip--mandal" },
  { key: "constituencies", cls: "bc-chip--const" },
  { key: "languages", cls: "bc-chip--lang" },
];
const TargetChips = ({ targets, wrap = false }) => {
  const t = targets || {};
  const all = TARGET_GROUPS.flatMap((g) => (t[g.key] || []).map((name) => ({ name, cls: g.cls })));
  if (!all.length) return <span className="bc-muted">-</span>;
  return (
    <div className={`bc-targets ${wrap ? "bc-targets--wrap" : ""}`}>
      {all.map((c, i) => (
        <span key={`${c.name}-${i}`} className={`bc-chip ${c.cls}`}>{c.name}</span>
      ))}
    </div>
  );
};

// Copies the salesBooking SlotVIew.handleDownload pattern: fetch -> blob -> <a download>.
const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const ext = (url.split(".").pop() || "pdf").split(/[?#]/)[0];
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${filename}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error(error);
    Toast("error", "Failed to download RO");
  }
};

/* Order details drawer: order meta + accumulated targets + RO download + a preview
   of the order's creatives. Unpicked orders get a "Pick Order" action that routes
   the CST into the existing order-placement (create-slot-order) screen. */
export const OrderDetailsDrawer = ({ order, onClose, onPick, picking }) => {
  const [creatives, setCreatives] = useState([]);
  const [adunits, setAdunits] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!order) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [creativesRes, adunitsRes] = await Promise.all([
          api({
            url: `/slotbooking/get-booking-creatives`,
            method: "POST",
            responseType: "json",
            data: { slot_id: order.order_id, page: 0, limit: 100 },
          }),
          api({
            url: `/slotbooking/get-order-adunits`,
            method: "POST",
            responseType: "json",
            data: { slot_id: order.order_id },
          }),
        ]);
        if (cancelled) return;
        if (creativesRes.code === 200) setCreatives(creativesRes.data?.data || []);
        if (adunitsRes.code === 200) setAdunits(adunitsRes.data?.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order]);

  if (!order) return null;

  return (
    <div className="bc-drawer__backdrop" onClick={onClose}>
      <aside className="bc-drawer bc-drawer--wide" onClick={(e) => e.stopPropagation()}>
        <header className="bc-drawer__head">
          <h3>{order.campaign_name || order.order_ref || `Slot #${order.order_id}`}</h3>
          <button className="bc-drawer__close" onClick={onClose} aria-label="Close">
            <sl-icon library="lucide" name="x"></sl-icon>
          </button>
        </header>

        <div className="bc-drawer__body">
          <dl className="bc-drawer__meta">
            <div><dt>Order ID</dt><dd>{order.order_ref || <span className="bc-muted">Not picked yet</span>}</dd></div>
            <div><dt>Status</dt><dd><OrderStatusChip status={order.order_status} /></dd></div>
            <div><dt>Picked</dt><dd><PickedBadge order={order} /></dd></div>
            <div><dt>Budget (RO Value)</dt><dd>{formatMoney(order.ro_value)}</dd></div>
            <div><dt>Brand</dt><dd>{order.brand_name || "-"}</dd></div>
            <div><dt>Company</dt><dd>{order.client_name || "-"}</dd></div>
            <div><dt>Sales Person</dt><dd>{order.sales_person?.name || "-"}</dd></div>
            <div><dt>Campaign</dt><dd>{order.campaign_name || "-"}</dd></div>
          </dl>

          <div className="bc-drawer__section">
            <h4>Ad Units {adunits.length ? `(${adunits.length})` : ""}</h4>
            {loading ? (
              <div className="bc-loading">Loading ad units...</div>
            ) : adunits.length ? (
              <div className="bc-adunits">
                {adunits.map((au) => (
                  <div key={au.internal_id} className="bc-adunit">
                    <div className="bc-adunit__head">
                      <span className="bc-adunit__id">Ad Unit #{au.internal_id}</span>
                      {au.campaign_type && <span className="bc-adunit__tag">{au.campaign_type}</span>}
                      {au.discounted && (
                        <span className="bc-count bc-count--warn" title="Sold below the rate card">
                          Discount
                        </span>
                      )}
                    </div>
                    <dl className="bc-adunit__grid">
                      {au.creative_type && <div><dt>Creative Type</dt><dd>{au.creative_type}</dd></div>}
                      {au.campaign_type === "CPD" && <div><dt>Position</dt><dd>{formatPositions(au.positions)}</dd></div>}
                      {au.campaign_type === "CPD" && au.slot != null && <div><dt>Slot</dt><dd>{SLOT_LABELS[au.slot] || "-"}</dd></div>}
                      {/* Approver view: per-day and overall, card AND sales price (overall sales = the unit's budget). */}
                      {adunitPriceRows(au).map((r) => (
                        <div key={r.key}><dt>{r.label}</dt><dd>{r.value}</dd></div>
                      ))}
                      {au.quantity != null && <div><dt>Quantity</dt><dd>{au.quantity}</dd></div>}
                    </dl>
                    {au.date_ranges?.length ? (
                      <div className="bc-adunit__block">
                        <dt>Date Ranges</dt>
                        <div className="bc-adunit__dates">
                          {au.date_ranges.map((dr, i) => (
                            <span key={i} className="bc-adunit__daterange">{formatDateRange(dr)}</span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <div className="bc-adunit__block">
                      <dt>Targets</dt>
                      <TargetChips targets={au.targets} wrap />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bc-muted">No ad units on this order.</div>
            )}
          </div>

          {order.rofile_url && (
            <div className="bc-drawer__section">
              <h4>Release Order</h4>
              <button
                className="bc-btn bc-btn--ghost"
                onClick={() => downloadFile(order.rofile_url, `${order.brand_name || "order"}_RO`)}
              >
                <sl-icon library="lucide" name="download"></sl-icon> Download RO
              </button>
            </div>
          )}

          <div className="bc-drawer__section">
            <h4>Creatives {creatives.length ? `(${creatives.length})` : ""}</h4>
            {loading ? (
              <div className="bc-loading">Loading creatives...</div>
            ) : creatives.length ? (
              <div className="bc-creatives-grid">
                {creatives.map((c) => {
                  const media = c.img_urls || null;
                  const isVideo = !!c.video_urls;
                  return (
                    <div key={c.bsc_id} className="bc-creative-card">
                      <div className="bc-creative-card__media">
                        {isVideo ? (
                          <video src={c.video_urls} poster={media || undefined} controls />
                        ) : media ? (
                          <img src={media} alt={c.creative_name} />
                        ) : (
                          <div className="bc-drawer__noimg">No preview</div>
                        )}
                      </div>
                      <div className="bc-creative-card__name">{c.creative_name || "Creative"}</div>
                      <StatusChip status={c.status} />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bc-muted">
                {order.creatives_by_cst
                  ? "Creatives by CST — this order was booked without creatives and is executed without them."
                  : "No creatives on this order."}
              </div>
            )}
          </div>
        </div>

        {!order.picked && (
          <footer className="bc-drawer__foot">
            {!order.all_creatives_final && (
              <span className="bc-foot-note">
                <sl-icon library="lucide" name="info"></sl-icon>
                All creatives must be final before this order can be picked.
              </span>
            )}
            <button
              className="bc-btn bc-btn--primary"
              onClick={() => onPick(order)}
              disabled={picking || !order.all_creatives_final}
              title={!order.all_creatives_final ? "All creatives must be final before picking" : ""}
            >
              <sl-icon library="lucide" name="check-check"></sl-icon> {picking ? "Picking..." : "Pick Order"}
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Orders tab: all booking-slot orders (picked + unpicked) so a CST    */
/* can review and pick unpicked ones.                                  */
/* ------------------------------------------------------------------ */
const OrdersSection = ({ salesFilterOptions }) => {
  const [rows, setRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [detailOrder, setDetailOrder] = useState(null);
  const [picking, setPicking] = useState(false);

  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [search, setSearch] = useState("");
  const [salesPersonIds, setSalesPersonIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const loadMoreCtxRef = useRef({});

  const fetchOrders = async (page, append = false) => {
    try {
      setLoader(true);
      const res = await api({
        url: `/slotbooking/get-booking-orders`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          startDate: dateRange.startDate || null,
          endDate: dateRange.endDate || null,
          search: search || null,
          salesPersonIds: salesPersonIds.length ? salesPersonIds.map((o) => o.value) : null,
          locationIds: locationIds.length ? locationIds.map((o) => o.value) : null,
        },
      });
      if (res.code === 200) {
        const newData = res.data?.data || [];
        const total = res.data?.total || 0;
        setRows((prev) => (append ? [...prev, ...newData] : newData));
        const totalPages = Math.ceil(total / config.PER_PAGE);
        setHasMore(page + 1 < totalPages);
        setTotalRecords(total);
      } else {
        Toast("error", res.data?.message || "Failed to load orders");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Technical error while loading orders");
    } finally {
      setLoader(false);
    }
  };

  loadMoreCtxRef.current = { loader, hasMore, currentPage };

  useEffect(() => {
    setCurrentPage(0);
    setRows([]);
    fetchOrders(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, search, salesPersonIds, locationIds]);

  const handleLoadMore = useCallback(() => {
    const ctx = loadMoreCtxRef.current;
    if (ctx.loader || !ctx.hasMore) return;
    const nextPage = ctx.currentPage + 1;
    setCurrentPage(nextPage);
    fetchOrders(nextPage, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sentinels = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!sentinels.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) handleLoadMore();
      },
      { threshold: 0.1 }
    );
    sentinels.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore, rows.length]);

  // Pick the order in place: this calls create-slot-order (which generates the order,
  // creates the ad-serving creatives, and the line items), then refreshes the list.
  const handlePick = async (order) => {
    try {
      setPicking(true);
      const res = await api({
        url: `/slotbooking/create-slot-order`,
        method: "POST",
        responseType: "json",
        data: { slot_id: order.order_id },
      });
      if (res.code === 200) {
        // The backend returns a message string only when it declined (e.g. already picked).
        if (res.data) {
          Toast("error", typeof res.data === "string" ? res.data : "Order already picked");
        } else {
          Toast("success", res.message || "Order picked successfully");
          setDetailOrder(null);
          setCurrentPage(0);
          setRows([]);
          fetchOrders(0, false);
        }
      } else {
        Toast("error", res.data?.message || "Failed to pick order");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Technical error while picking order");
    } finally {
      setPicking(false);
    }
  };

  const actionsCell = (row) => (
    <div className="bc-actions">
      {row.rofile_url && (
        <button
          className="bc-btn bc-btn--ghost"
          title="Download RO"
          onClick={() => downloadFile(row.rofile_url, `${row.brand_name || "order"}_RO`)}
        >
          <sl-icon library="lucide" name="download"></sl-icon> RO
        </button>
      )}
      <button className="bc-btn bc-btn--ghost" onClick={() => setDetailOrder(row)}>
        <sl-icon library="lucide" name="eye"></sl-icon> View
      </button>
    </div>
  );

  return (
    <>
      {/* Filters */}
      <div className="bc-filters">
        <div className="bc-filters__field bc-filters__search">
          <sl-icon library="lucide" name="search"></sl-icon>
          <input
            type="text"
            placeholder="Search order / brand / company / campaign"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <SalesFilterFields
          salesPersonOptions={salesFilterOptions.salesPersonOptions}
          locationOptions={salesFilterOptions.locationOptions}
          salesPersonIds={salesPersonIds}
          onSalesPersonIdsChange={setSalesPersonIds}
          locationIds={locationIds}
          onLocationIdsChange={setLocationIds}
        />
        <div className="bc-filters__field bc-filters__dates">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange((p) => ({ ...p, startDate: e.target.value }))}
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
          />
        </div>
      </div>

      <div className="bc-total">Total Orders: {totalRecords}</div>

      {/* Mobile cards */}
      <div className="lineitems-mobile-view bc-mobile">
        {loader && rows.length === 0 ? (
          <div className="bc-loading">Loading...</div>
        ) : rows.length > 0 ? (
          rows.map((row, idx) => {
            const created = alphaformatDateTime(row.created_time);
            return (
              <div key={row.order_id} className="bc-card">
                <div className="bc-card__head">
                  <span className="bc-card__sno">#{String(idx + 1).padStart(2, "0")}</span>
                  <span className="bc-actions">
                    <CstCreativesBadge order={row} />
                    <PickedBadge order={row} />
                  </span>
                </div>
                <div className="bc-card__row"><span>Order ID</span><b>{row.order_ref || "-"}</b></div>
                <div className="bc-card__row"><span>Status</span><b><OrderStatusChip status={row.order_status} /></b></div>
                <div className="bc-card__row"><span>Sales Person</span><b>{row.sales_person?.name || "-"}</b></div>
                <div className="bc-card__row"><span>Brand</span><b>{row.brand_name || "-"}</b></div>
                <div className="bc-card__row"><span>Company</span><b>{row.client_name || "-"}</b></div>
                <div className="bc-card__row"><span>Campaign</span><b>{row.campaign_name || "-"}</b></div>
                <div className="bc-card__row"><span>Budget</span><b>{formatMoney(row.ro_value)}</b></div>
                <div className="bc-card__row"><span>Created</span><b>{created.dateStr} | {created.timeStr}</b></div>
                <div className="bc-card__row bc-card__row--col"><span>Targets</span><TargetChips targets={row.targets} wrap /></div>
                <div className="bc-card__foot">
                  {row.rofile_url && (
                    <button className="bc-btn bc-btn--ghost" onClick={() => downloadFile(row.rofile_url, `${row.brand_name || "order"}_RO`)}>
                      <sl-icon library="lucide" name="download"></sl-icon> RO
                    </button>
                  )}
                  <button className="bc-btn bc-btn--ghost" onClick={() => setDetailOrder(row)}>View Details</button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bc-loading">No Data Found</div>
        )}
        {hasMore && !loader && <div ref={mobileSentinelRef} className="h-6 w-full" />}
      </div>

      {/* Desktop table */}
      <div className="mobHide bc-table-wrap">
        <ScrollContainer>
          <table className="table table-bordered bc-table">
            <thead>
              <tr className="bg-gray-100">
                <th>Sales Person Details</th>
                <th>Order Date</th>
                <th>Order ID</th>
                <th>Brand</th>
                <th>Company</th>
                <th>Campaign Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Budget</th>
                <th>Targets</th>
                <th>Picked</th>
                <th>Actions</th>
              </tr>
            </thead>
            {loader && rows.length === 0 ? (
              <TableLoader colspan="12" />
            ) : rows.length > 0 ? (
              rows.map((row, idx) => (
                <tbody key={row.order_id}>
                  <tr className={`border-t ${idx % 2 === 0 ? "" : "bg-gray-100"}`}>
                    <td>{salesPersonCell(row)}</td>
                    <td>{dateCell(row.created_time)}</td>
                    <td className="bc-creative-name">{row.order_ref || <span className="bc-muted">-</span>}</td>
                    <td>{row.brand_name || "-"}</td>
                    <td>{row.client_name || "-"}</td>
                    <td>{row.campaign_name || "-"}</td>
                    <td>{dateCell(row.start_date)}</td>
                    <td>{dateCell(row.end_date)}</td>
                    <td className="bc-creative-name">{formatMoney(row.ro_value)}</td>
                    <td><TargetChips targets={row.targets} /></td>
                    <td>
                      <div className="bc-actions">
                        <PickedBadge order={row} />
                        <CstCreativesBadge order={row} />
                      </div>
                    </td>
                    <td>{actionsCell(row)}</td>
                  </tr>
                </tbody>
              ))
            ) : (
              <NoDataFound colspan="12" />
            )}
          </table>
        </ScrollContainer>
      </div>

      {hasMore && !loader && <div ref={sentinelRef} className="h-6 w-full" />}
      {loader && rows.length > 0 && (
        <div className="flex justify-center py-3">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      <OrderDetailsDrawer order={detailOrder} onClose={() => setDetailOrder(null)} onPick={handlePick} picking={picking} />
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Creatives tab: assign a designer AND a content writer per booking-  */
/* slot creative, and review/approve/reject the content writer's       */
/* submitted drafts + any pending sales-person requests via the shared */
/* content-admin DetailDrawer.                                         */
/* ------------------------------------------------------------------ */
const CreativesSection = ({ salesFilterOptions }) => {
  const [designers, setDesigners] = useState([]);
  const [rows, setRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const [detailRow, setDetailRow] = useState(null);

  // Content writers (for ContentWriterAssignControl) — fetched once on
  // mount, like designers: the roster barely changes within a session and the
  // control needs it the instant it's opened.
  const [writers, setWriters] = useState([]);
  const [writersLoading, setWritersLoading] = useState(true);

  // Approve/reject confirm-dialog state — mirrors the retired CSTAdminHome,
  // minus tab reconciliation (this screen is one flat list, not status-keyed
  // tabs).
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectTouched, setRejectTouched] = useState(false);
  // The bsc_id whose approve/reject call is in flight, or null. One at a time:
  // both actions are terminal for the row, so overlapping them makes no sense.
  const [actionBusy, setActionBusy] = useState(null);

  // Filters
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const [contentStatusFilter, setContentStatusFilter] = useState(CONTENT_STATUS_OPTIONS[0]);
  const [designerFilter, setDesignerFilter] = useState(null);
  const [adTypeFilter, setAdTypeFilter] = useState(AD_TYPE_OPTIONS[0]);
  const [creativeTypeFilter, setCreativeTypeFilter] = useState(SUBTYPE_OPTIONS[0]);
  // Separate, independent, optional, multi-select filters (AND'd together
  // when both are set) — see the SalesFilterFields component below.
  const [salesPersonIds, setSalesPersonIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [search, setSearch] = useState("");
  const [sortByLatestActivity, setSortByLatestActivity] = useState(() => {
    const saved = localStorage.getItem(SORT_BY_LATEST_ACTIVITY_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem(SORT_BY_LATEST_ACTIVITY_KEY, JSON.stringify(sortByLatestActivity));
  }, [sortByLatestActivity]);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const loadMoreCtxRef = useRef({});

  const fetchBookingCreatives = async (page, append = false) => {
    try {
      setLoader(true);
      const res = await api({
        url: `/slotbooking/get-booking-creatives`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          startDate: dateRange.startDate || null,
          endDate: dateRange.endDate || null,
          status: status ? status.value : null,
          // The "Unassigned" pseudo-status (value 0) means no
          // booking_slot_creative_content row exists at all — the table never
          // stores a literal status=0 row, so that case needs its own boolean
          // flag rather than being sent as content_status: 0 (which would
          // match zero real rows).
          content_status: contentStatusFilter && contentStatusFilter.value !== 0 ? contentStatusFilter.value : null,
          is_unassigned_content: contentStatusFilter?.value === 0 ? true : null,
          designer_id: designerFilter ? designerFilter.value : null,
          creative_type: adTypeFilter ? adTypeFilter.value : null,
          sub_type: creativeTypeFilter ? creativeTypeFilter.value : null,
          search: search || null,
          salesPersonIds: salesPersonIds.length ? salesPersonIds.map((o) => o.value) : null,
          locationIds: locationIds.length ? locationIds.map((o) => o.value) : null,
          sort_by_latest_activity: sortByLatestActivity,
        },
      });
      if (res.code === 200) {
        const newData = res.data?.data || [];
        const total = res.data?.total || 0;
        setRows((prev) => (append ? [...prev, ...newData] : newData));
        const totalPages = Math.ceil(total / config.PER_PAGE);
        setHasMore(page + 1 < totalPages);
        setTotalRecords(total);
      } else {
        Toast("error", res.data?.message || "Failed to load creatives");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Technical error while loading creatives");
    } finally {
      setLoader(false);
    }
  };

  const fetchDesigners = async () => {
    try {
      const res = await api({
        url: `/alpha_cst_designer/getDesigners`,
        method: "POST",
        responseType: "json",
        data: {},
      });
      if (res.code === 200) setDesigners(res.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Content writers, fetched once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setWritersLoading(true);
        const data = await fetchContentWriters();
        if (cancelled) return;
        setWriters(Array.isArray(data) ? data : []);
      } catch (error) {
        if (cancelled) return;
        console.error(error);
        Toast("error", error?.message || "Unable to load the content writers.");
      } finally {
        if (!cancelled) setWritersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    fetchDesigners();
    setCurrentPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  loadMoreCtxRef.current = { loader, hasMore, currentPage };

  // Reset + refetch when a filter changes.
  useEffect(() => {
    setCurrentPage(0);
    setRows([]);
    fetchBookingCreatives(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, status, contentStatusFilter, designerFilter, adTypeFilter, creativeTypeFilter, salesPersonIds, locationIds, search, sortByLatestActivity]);

  const handleLoadMore = useCallback(() => {
    const ctx = loadMoreCtxRef.current;
    if (ctx.loader || !ctx.hasMore) return;
    const nextPage = ctx.currentPage + 1;
    setCurrentPage(nextPage);
    fetchBookingCreatives(nextPage, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sentinels = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!sentinels.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) handleLoadMore();
      },
      { threshold: 0.1 }
    );
    sentinels.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore, rows.length]);

  // Assign or reassign the designer for one creative. Called by the shared
  // DesignerAssignControl from the table cell, the mobile card and the detail
  // drawer's Info tab — the control owns its own reveal/submitting state and
  // only needs a boolean back.
  const handleAssignDesigner = async (row, designerId, designerName) => {
    const isReassign = !!row.designer_name;
    try {
      const res = await api({
        url: `/slotbooking/assign-designer`,
        method: "POST",
        responseType: "json",
        data: { bsc_id: row.bsc_id, designer_id: designerId, designer_name: designerName },
      });
      if (res.code === 200) {
        Toast("success", isReassign ? "Designer reassigned successfully" : "Designer assigned successfully");
        patchContentRow(row.bsc_id, {
          designer_id: designerId,
          designer_name: designerName,
          status: CREATIVE_STATUS.DESIGNER_ASSIGNED,
        });
        return true;
      }
      Toast("error", res.data?.message || "Failed to assign designer");
      return false;
    } catch (error) {
      console.error(error);
      Toast("error", "Technical error while assigning designer");
      return false;
    }
  };

  const designerOptions = designers.map((d) => ({ value: d.designer_id, label: d.name }));

  // A FINAL creative's designer is locked — the control renders it read-only.
  const renderDesignerControl = (row) => (
    <DesignerAssignControl
      row={row}
      designers={designers}
      locked={row.status === CREATIVE_STATUS.FINAL}
      onAssign={handleAssignDesigner}
    />
  );

  // Patches one row's content fields both in the list and (if open) the open
  // drawer, so an assign/approve/reject call updates what's on screen without
  // a reload. `detailRow` holds the RAW (un-adapted) row shape, same as `rows`.
  const patchContentRow = (bscId, patch) => {
    setRows((prev) => prev.map((r) => (r.bsc_id === bscId ? { ...r, ...patch } : r)));
    setDetailRow((prev) => (prev && prev.bsc_id === bscId ? { ...prev, ...patch } : prev));
  };

  // Assign or reassign the content writer for one creative. Called by the
  // shared ContentWriterAssignControl from the table cell, the mobile card
  // and the detail drawer's Info tab — mirrors handleAssignDesigner above,
  // the control owns its own reveal/submitting state and only needs a
  // boolean back.
  const handleAssignWriter = async (row, writerId, writerName, instructions) => {
    const isReassign = !!row.content_writer_name;
    try {
      const data = await assignContentWriter(row.bsc_id, writerId, instructions);
      Toast("success", isReassign ? "Content writer reassigned successfully" : "Content writer assigned successfully");
      patchContentRow(row.bsc_id, {
        content_writer_id: data?.content_writer_id ?? writerId,
        content_writer_name: data?.content_writer_name ?? writerName,
        content_status: data?.status ?? 1,
        content_instructions: instructions,
      });
      return true;
    } catch (error) {
      console.error(error);
      Toast("error", error?.message || "Unable to assign this creative.");
      return false;
    }
  };

  const requestApproveContent = (row, variationIndex) => {
    if (!row) return;
    // Defensive: DetailDrawer already gates on this, but a stale row that
    // moved on server-side must never be able to fire the call.
    if (!canApprove(row.content_status)) return;
    setApproveTarget({ row, variationIndex: Number(variationIndex) || 0 });
  };

  const confirmApproveContent = async () => {
    const target = approveTarget;
    const row = target?.row;
    if (!row || actionBusy != null) return;
    if (!canApprove(row.content_status)) {
      setApproveTarget(null);
      return;
    }
    setActionBusy(row.bsc_id);
    try {
      const data = await approveContent(row.bsc_id, target.variationIndex);
      patchContentRow(row.bsc_id, { content_status: data?.status ?? 5 });
      setApproveTarget(null);
      Toast("success", `Draft ${target.variationIndex + 1} approved`);
    } catch (error) {
      console.error(error);
      Toast("error", error?.message || "Unable to approve this content.");
    } finally {
      setActionBusy(null);
    }
  };

  const requestRejectContent = (row) => {
    if (!row) return;
    if (!canReject(row.content_status)) return;
    setRejectRow(row);
  };

  // The reason box is scratch state for one dialog session — wipe it whenever
  // the dialog opens or closes so the next reject never inherits old text.
  useEffect(() => {
    setRejectReason("");
    setRejectTouched(false);
  }, [rejectRow]);

  const rejectReasonValid = rejectReason.trim().length > 0;

  const confirmRejectContent = async () => {
    const row = rejectRow;
    if (!row || actionBusy != null) return;
    if (!rejectReason.trim()) {
      setRejectTouched(true);
      return;
    }
    if (!canReject(row.content_status)) {
      setRejectRow(null);
      return;
    }
    setActionBusy(row.bsc_id);
    try {
      const data = await rejectContent(row.bsc_id, rejectReason);
      patchContentRow(row.bsc_id, { content_status: data?.status ?? 4 });
      setRejectRow(null);
      setRejectReason("");
      setRejectTouched(false);
      Toast("success", "Sent back to the content writer");
    } catch (error) {
      console.error(error);
      Toast("error", error?.message || "Unable to send this back for rework.");
    } finally {
      setActionBusy(null);
    }
  };

  const renderContentWriterControl = (row) => (
    <ContentWriterAssignControl
      row={row}
      writers={writers}
      writersLoading={writersLoading}
      onAssign={handleAssignWriter}
    />
  );

  const drawerRow = toContentDrawerRow(detailRow);

  return (
    <>
      {/* Filters */}
      <div className="bc-filters">
        <div className="bc-filters__field bc-filters__search">
          <sl-icon library="lucide" name="search"></sl-icon>
          <input
            type="text"
            placeholder="Search creative / brand / client / campaign"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="bc-filters__field">
          <Select
            classNamePrefix="bc-rs"
            placeholder="Design Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="bc-filters__field">
          <Select
            classNamePrefix="bc-rs"
            placeholder="Content Status"
            options={CONTENT_STATUS_OPTIONS}
            value={contentStatusFilter}
            onChange={setContentStatusFilter}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="bc-filters__field">
          <Select
            classNamePrefix="bc-rs"
            placeholder="Designer"
            isClearable
            options={designerOptions}
            value={designerFilter}
            onChange={setDesignerFilter}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <SalesFilterFields
          salesPersonOptions={salesFilterOptions.salesPersonOptions}
          locationOptions={salesFilterOptions.locationOptions}
          salesPersonIds={salesPersonIds}
          onSalesPersonIdsChange={setSalesPersonIds}
          locationIds={locationIds}
          onLocationIdsChange={setLocationIds}
        />
        <div className="bc-filters__field">
          <Select
            classNamePrefix="bc-rs"
            placeholder="Ad Type"
            options={AD_TYPE_OPTIONS}
            value={adTypeFilter}
            onChange={setAdTypeFilter}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="bc-filters__field">
          <Select
            classNamePrefix="bc-rs"
            placeholder="Creative Type"
            options={SUBTYPE_OPTIONS}
            value={creativeTypeFilter}
            onChange={setCreativeTypeFilter}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
        <div className="bc-filters__field bc-filters__dates">
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange((p) => ({ ...p, startDate: e.target.value }))}
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange((p) => ({ ...p, endDate: e.target.value }))}
          />
        </div>
      </div>

      <div className="bc-total">
        Total Creatives: {totalRecords}
        <Toggle
          title="Sort by Latest Activity"
          open={sortByLatestActivity ? 1 : 0}
          setOpen={(v) => setSortByLatestActivity(Boolean(v))}
        />
      </div>

      {/* Mobile cards */}
      <div className="lineitems-mobile-view bc-mobile">
        {loader && rows.length === 0 ? (
          <div className="bc-loading">Loading...</div>
        ) : rows.length > 0 ? (
          rows.map((row, idx) => {
            const created = alphaformatDateTime(row.created_time);
            return (
              <div key={row.bsc_id} className="bc-card">
                <div className="bc-card__head">
                  <span className="bc-card__sno">#{String(idx + 1).padStart(2, "0")}</span>
                  <StatusChip status={row.status} />
                </div>
                <div className="bc-card__row"><span>Creative</span><b>{row.creative_name}</b></div>
                <div className="bc-card__row"><span>Sales Person</span><b>{row.sales_person?.name || "-"}</b></div>
                <div className="bc-card__row"><span>Brand</span><b>{row.brand_name || "-"}</b></div>
                <div className="bc-card__row"><span>Company</span><b>{row.client_name || "-"}</b></div>
                <div className="bc-card__row"><span>Campaign</span><b>{row.campaign_name || "-"}</b></div>
                <div className="bc-card__row"><span>Created</span><b>{created.dateStr} | {created.timeStr}</b></div>
                <div className="bc-card__row"><span>Creative Type</span><b>{row.sub_type_label || SUBTYPE_LABELS[row.sub_type] || row.sub_type || "-"}</b></div>
                <div className="bc-card__assign">{renderDesignerControl(row)}</div>
                <div className="bc-card__assign">{renderContentWriterControl(row)}</div>
                <div className="bc-card__foot">
                  <button className="bc-btn bc-btn--ghost" onClick={() => setDetailRow(row)}>View Details</button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bc-loading">No Data Found</div>
        )}
        {hasMore && !loader && <div ref={mobileSentinelRef} className="h-6 w-full" />}
      </div>

      {/* Desktop table */}
      <div className="mobHide bc-table-wrap">
        <ScrollContainer>
          <table className="table table-bordered bc-table">
            <thead>
              <tr className="bg-gray-100">
                <th>Sales Person Details</th>
                <th>Ad Creation Date</th>
                <th>Creative Name</th>
                <th>Brand</th>
                <th>Company</th>
                <th>Campaign Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Ad Type</th>
                <th>Creative Type</th>
                <th>Assign to Designer</th>
                <th>Design Status</th>
                <th>Assign Content Writer</th>
                <th>Action</th>
              </tr>
            </thead>
            {loader && rows.length === 0 ? (
              <TableLoader colspan="14" />
            ) : rows.length > 0 ? (
              rows.map((row, idx) => (
                <tbody key={row.bsc_id}>
                  <tr className={`border-t ${idx % 2 === 0 ? "" : "bg-gray-100"}`}>
                    <td>{salesPersonCell(row)}</td>
                    <td>{dateCell(row.created_time)}</td>
                    <td className="bc-creative-name">{row.creative_name || "-"}</td>
                    <td>{row.brand_name || "-"}</td>
                    <td>{row.client_name || "-"}</td>
                    <td>{row.campaign_name || "-"}</td>
                    <td>{dateCell(row.start_date)}</td>
                    <td>{dateCell(row.end_date)}</td>
                    <td>{AD_TYPE_LABELS[row.creative_type] || row.creative_type || "-"}</td>
                    <td>{row.sub_type_label || SUBTYPE_LABELS[row.sub_type] || row.sub_type || "-"}</td>
                    <td>{renderDesignerControl(row)}</td>
                    <td><StatusChip status={row.status} /></td>
                    <td>{renderContentWriterControl(row)}</td>
                    <td>
                      <div className="bc-actions">
                        <button className="bc-btn bc-btn--ghost" onClick={() => setDetailRow(row)}>
                          <sl-icon library="lucide" name="eye"></sl-icon>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              ))
            ) : (
              <NoDataFound colspan="14" />
            )}
          </table>
        </ScrollContainer>
      </div>

      {hasMore && !loader && <div ref={sentinelRef} className="h-6 w-full" />}
      {loader && rows.length > 0 && (
        <div className="flex justify-center py-3">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      <DetailDrawer
        open={!!detailRow}
        row={drawerRow}
        onClose={() => setDetailRow(null)}
        onRequestApprove={requestApproveContent}
        onRequestReject={requestRejectContent}
        busy={detailRow ? Number(actionBusy) === Number(detailRow.bsc_id) : false}
        designers={designers}
        designerAssignLocked={detailRow ? detailRow.status === CREATIVE_STATUS.FINAL : false}
        onAssignDesigner={handleAssignDesigner}
        writers={writers}
        writersLoading={writersLoading}
        onAssignWriter={handleAssignWriter}
      />

      <ConfirmDialog
        open={!!approveTarget}
        title="Approve this draft?"
        message={
          approveTarget
            ? `Draft ${approveTarget.variationIndex + 1} of "${creativeLabel(approveTarget.row)}" becomes the approved content. This closes the content work for this creative.`
            : ""
        }
        confirmLabel="Approve draft"
        cancelLabel="Not yet"
        tone="primary"
        busy={actionBusy != null}
        onCancel={() => setApproveTarget(null)}
        onConfirm={confirmApproveContent}
      />

      <ConfirmDialog
        open={!!rejectRow}
        title="Send back for rework?"
        message={
          rejectRow
            ? `"${creativeLabel(rejectRow)}" goes back to the content writer with your reason. They can submit a fresh set of drafts.`
            : ""
        }
        confirmLabel="Send back"
        cancelLabel="Cancel"
        tone="danger"
        busy={actionBusy != null}
        onCancel={() => setRejectRow(null)}
        onConfirm={confirmRejectContent}
      >
        <label className="block text-sm font-medium text-slate-700" htmlFor="bc-reject-reason">
          Reason for sending back <span className="text-rose-600">*</span>
        </label>
        <textarea
          id="bc-reject-reason"
          rows={4}
          value={rejectReason}
          disabled={actionBusy != null}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Tell the writer what to change — be specific enough that they can act on it."
          className="mt-1 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
        />
        <div className="mt-1 flex items-start justify-between gap-3">
          <p className="text-xs text-slate-500">
            The writer sees this word for word. A line or two is usually enough.
          </p>
          <span className="shrink-0 text-xs text-slate-400">{rejectReason.trim().length}</span>
        </div>
        {rejectTouched && !rejectReasonValid && (
          <p className="mt-1 text-xs font-medium text-rose-600">
            Add a reason before sending this back.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
};

/* ------------------------------------------------------------------ */
/* New Device Login Access tab: release a sales person's order-booking */
/* account to a new handset.                                           */
/*                                                                     */
/* A sales person's account is bound to ONE phone, identified by the   */
/* marketing id (mid) the native shell reports at sign-in. Signing in  */
/* from a different phone is refused by the order-booking API          */
/* (ERR_NEW_DEVICE_LOGIN, "New device login. Please contact admin.")   */
/* and the attempt is filed in sales_app_user_new_device_requests.     */
/* This tab is that queue.                                             */
/*                                                                     */
/* Allow does NOT bind the new phone here. It snapshots the currently  */
/* bound device into history and clears the bound mid; the sales       */
/* person's next sign-in then binds their new phone itself. So after   */
/* allowing, tell them to sign in again — nothing happens on its own.  */
/* ------------------------------------------------------------------ */
const DEVICE_STATUS_META = {
  0: { label: "Pending", cls: "bc-count--warn" },
  1: { label: "Allowed", cls: "bc-count--ok" },
  2: { label: "Rejected", cls: "bc-count--warn" },
};

const DEVICE_STATUS_OPTIONS = [
  { value: 0, label: "Pending" },
  { value: 1, label: "Allowed" },
  { value: 2, label: "Rejected" },
  { value: null, label: "All" },
];

const DeviceStatusChip = ({ status }) => {
  const meta = DEVICE_STATUS_META[status] || { label: "-", cls: "" };
  return <span className={`bc-count ${meta.cls}`}>{meta.label}</span>;
};

// A mid is a long opaque device id — show enough to compare two by eye without
// letting it blow the column width out.
const MidCell = ({ value, muted }) => {
  if (!value) return <span className="bc-muted">{muted || "-"}</span>;
  return (
    <span className="bc-creative-name" title={value}>
      {value.length > 18 ? `${value.slice(0, 18)}…` : value}
    </span>
  );
};

// Who actioned the request, and when. Pending rows have neither.
const DecidedByCell = ({ row }) => {
  if (Number(row.status) === 0) return <span className="bc-muted">-</span>;
  const when = row.decided_at ? alphaformatDateTime(row.decided_at) : null;
  return (
    <div className="bc-person">
      <span className="bc-person__name">{row.decided_by_name || (row.decided_by ? `#${row.decided_by}` : "-")}</span>
      {when && <span className="bc-person__phone">{when.dateStr} | {when.timeStr}</span>}
    </div>
  );
};

const DeviceAccessSection = () => {
  const [rows, setRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(DEVICE_STATUS_OPTIONS[0]);

  // { row, decision } — the pending confirmation, or null.
  const [confirming, setConfirming] = useState(null);
  const [deciding, setDeciding] = useState(false);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const loadMoreCtxRef = useRef({});

  const fetchRequests = async (page, append = false) => {
    try {
      setLoader(true);
      const res = await api({
        url: `/slotbooking/get-device-access-requests`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          search: search || null,
          status: statusFilter?.value ?? null,
        },
      });
      if (res.code === 200) {
        const newData = res.data?.data || [];
        const total = res.data?.total || 0;
        setRows((prev) => (append ? [...prev, ...newData] : newData));
        setHasMore(page + 1 < Math.ceil(total / config.PER_PAGE));
        setTotalRecords(total);
      } else {
        Toast("error", res.data?.message || "Failed to load device access requests");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Technical error while loading device access requests");
    } finally {
      setLoader(false);
    }
  };

  loadMoreCtxRef.current = { loader, hasMore, currentPage };

  useEffect(() => {
    setCurrentPage(0);
    setRows([]);
    fetchRequests(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const handleLoadMore = useCallback(() => {
    const ctx = loadMoreCtxRef.current;
    if (ctx.loader || !ctx.hasMore) return;
    const nextPage = ctx.currentPage + 1;
    setCurrentPage(nextPage);
    fetchRequests(nextPage, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sentinels = [sentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!sentinels.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) handleLoadMore();
      },
      { threshold: 0.1 }
    );
    sentinels.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [hasMore, handleLoadMore, rows.length]);

  const handleDecide = async () => {
    if (!confirming) return;
    const { row, decision } = confirming;
    try {
      setDeciding(true);
      const res = await api({
        url: `/slotbooking/decide-device-access`,
        method: "POST",
        responseType: "json",
        data: { undr_id: row.undr_id, decision },
      });
      if (res.code === 200) {
        Toast(
          "success",
          decision === "allow"
            ? `Access released. Ask ${row.sales_person?.name || "the sales person"} to sign in again on the new phone.`
            : "Request rejected"
        );
        setConfirming(null);
        setCurrentPage(0);
        setRows([]);
        fetchRequests(0, false);
      } else {
        Toast("error", res.data?.message || res.message || "Failed to update the request");
      }
    } catch (error) {
      console.error(error);
      Toast("error", "Technical error while updating the request");
    } finally {
      setDeciding(false);
    }
  };

  const personCell = (row) => {
    const sp = row.sales_person || {};
    return (
      <div className="bc-person">
        <span className="bc-person__name">
          <sl-icon library="lucide" name="user" style={{ fontSize: "14px" }}></sl-icon>
          {sp.name || `#${sp.user_id}`}
        </span>
        {sp.mobile && (
          <span className="bc-person__phone">
            <sl-icon library="lucide" name="phone" style={{ fontSize: "12px" }}></sl-icon>
            {sp.mobile}
          </span>
        )}
        {sp.email && <span className="bc-person__phone">{sp.email}</span>}
      </div>
    );
  };

  const actionsCell = (row) => {
    if (Number(row.status) !== 0) return <span className="bc-muted">-</span>;
    return (
      <div className="bc-actions">
        <button className="bc-btn bc-btn--ghost" onClick={() => setConfirming({ row, decision: "allow" })}>
          <sl-icon library="lucide" name="smartphone"></sl-icon> Allow
        </button>
        <button className="bc-btn bc-btn--ghost" onClick={() => setConfirming({ row, decision: "reject" })}>
          <sl-icon library="lucide" name="x"></sl-icon> Reject
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="bc-filters">
        <div className="bc-filters__field bc-filters__search">
          <sl-icon library="lucide" name="search"></sl-icon>
          <input
            type="text"
            placeholder="Search name / email / mobile / device id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="bc-filters__field">
          <Select
            classNamePrefix="bc-rs"
            placeholder="Status"
            options={DEVICE_STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            isSearchable={false}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>

      <div className="bc-total">Total Requests: {totalRecords}</div>

      {/* Mobile cards */}
      <div className="lineitems-mobile-view bc-mobile">
        {loader && rows.length === 0 ? (
          <div className="bc-loading">Loading...</div>
        ) : rows.length > 0 ? (
          rows.map((row) => {
            const asked = alphaformatDateTime(row.updated_at || row.created_at);
            return (
              <div key={row.undr_id} className="bc-card">
                <div className="bc-card__head">{personCell(row)}</div>
                <div className="bc-card__row"><span>Status</span><b><DeviceStatusChip status={row.status} /></b></div>
                <div className="bc-card__row"><span>New Device</span><b><MidCell value={row.requested_mid} /></b></div>
                <div className="bc-card__row"><span>Bound Device</span><b><MidCell value={row.current_mid} muted="Released — awaiting sign-in" /></b></div>
                <div className="bc-card__row"><span>Attempts</span><b>{row.attempts}</b></div>
                <div className="bc-card__row"><span>IP</span><b>{row.ip || "-"}</b></div>
                <div className="bc-card__row"><span>Last Attempt</span><b>{asked.dateStr} | {asked.timeStr}</b></div>
                {Number(row.status) !== 0 && (
                  <div className="bc-card__row"><span>Decided By</span><b><DecidedByCell row={row} /></b></div>
                )}
                <div className="bc-card__foot">{actionsCell(row)}</div>
              </div>
            );
          })
        ) : (
          <div className="bc-loading">No Data Found</div>
        )}
        {hasMore && !loader && <div ref={mobileSentinelRef} className="h-6 w-full" />}
      </div>

      {/* Desktop table */}
      <div className="mobHide bc-table-wrap">
        <ScrollContainer>
          <table className="table table-bordered bc-table">
            <thead>
              <tr className="bg-gray-100">
                <th>Sales Person Details</th>
                <th>Team</th>
                <th>New Device Id</th>
                <th>Currently Bound</th>
                <th>Attempts</th>
                <th>IP</th>
                <th>Last Attempt</th>
                <th>Status</th>
                <th>Decided By</th>
                <th>Actions</th>
              </tr>
            </thead>
            {loader && rows.length === 0 ? (
              <TableLoader colspan="10" />
            ) : rows.length > 0 ? (
              rows.map((row, idx) => (
                <tbody key={row.undr_id}>
                  <tr className={`border-t ${idx % 2 === 0 ? "" : "bg-gray-100"}`}>
                    <td>{personCell(row)}</td>
                    <td>{row.sales_person?.team || "-"}</td>
                    <td><MidCell value={row.requested_mid} /></td>
                    <td><MidCell value={row.current_mid} muted="Released — awaiting sign-in" /></td>
                    <td>{row.attempts}</td>
                    <td>{row.ip || "-"}</td>
                    <td>{dateCell(row.updated_at || row.created_at)}</td>
                    <td><DeviceStatusChip status={row.status} /></td>
                    <td><DecidedByCell row={row} /></td>
                    <td>{actionsCell(row)}</td>
                  </tr>
                </tbody>
              ))
            ) : (
              <NoDataFound colspan="10" />
            )}
          </table>
        </ScrollContainer>
      </div>

      {hasMore && !loader && <div ref={sentinelRef} className="h-6 w-full" />}
      {loader && rows.length > 0 && (
        <div className="flex justify-center py-3">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirming)}
        title={confirming?.decision === "allow" ? "Allow new device login?" : "Reject this request?"}
        message={
          confirming?.decision === "allow"
            ? `This releases ${confirming?.row?.sales_person?.name || "this sales person"}'s account from their current phone. Their next sign-in binds whichever phone they use, so confirm they are the one asking before allowing.`
            : `${confirming?.row?.sales_person?.name || "This sales person"} will keep being refused on the new phone.`
        }
        confirmLabel={confirming?.decision === "allow" ? "Allow" : "Reject"}
        tone={confirming?.decision === "allow" ? "primary" : "danger"}
        busy={deciding}
        onConfirm={handleDecide}
        onCancel={() => (deciding ? null : setConfirming(null))}
      />
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Container: "Creative Order Booking" tabs.                           */
/* ------------------------------------------------------------------ */
const TABS = [
  { key: "orders", label: "Orders", icon: "clipboard-list" },
  { key: "creatives", label: "Creatives", icon: "palette" },
  { key: "device-access", label: "New Device Login Access", icon: "smartphone" },
];

// Filter-panel picker data (salesperson / sales-location), shared by both tabs
// so switching tabs doesn't refetch it. Both filters are separate, multi-select
// and optional — see /slotbooking/get-booking-filter-options.
const useSalesFilterOptions = () => {
  const [salesPersonOptions, setSalesPersonOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api({
          url: `/slotbooking/get-booking-filter-options`,
          method: "POST",
          responseType: "json",
          data: {},
        });
        if (cancelled || res.code !== 200) return;
        setSalesPersonOptions(res.data?.salesPersons || []);
        setLocationOptions(res.data?.locations || []);
      } catch (error) {
        console.error(error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { salesPersonOptions, locationOptions };
};

// The salesperson / sales-location multi-select pair, identical on both tabs.
const SalesFilterFields = ({ salesPersonOptions, locationOptions, salesPersonIds, onSalesPersonIdsChange, locationIds, onLocationIdsChange }) => (
  <>
    <div className="bc-filters__field">
      <Select
        classNamePrefix="bc-rs"
        placeholder="Sales Person"
        isMulti
        isClearable
        options={salesPersonOptions}
        value={salesPersonIds}
        onChange={onSalesPersonIdsChange}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
    <div className="bc-filters__field">
      <Select
        classNamePrefix="bc-rs"
        placeholder="Sales Location"
        isMulti
        isClearable
        options={locationOptions}
        value={locationIds}
        onChange={onLocationIdsChange}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  </>
);

const BookingCreativesAssign = () => {
  const [activeTab, setActiveTab] = useState("orders");
  const salesFilterOptions = useSalesFilterOptions();

  return (
    <div className="bc-wrap">
      <div className="bc-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`bc-tab ${activeTab === tab.key ? "bc-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <sl-icon library="lucide" name={tab.icon}></sl-icon>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "orders" && <OrdersSection salesFilterOptions={salesFilterOptions} />}
      {activeTab === "creatives" && <CreativesSection salesFilterOptions={salesFilterOptions} />}
      {activeTab === "device-access" && <DeviceAccessSection />}
    </div>
  );
};

export default BookingCreativesAssign;
